import { escapeHtml } from '@/utils/trackHoverPopup'

const ROUTE_ICON_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" ` +
  `stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" ` +
  `style="color:#111827;flex-shrink:0;" aria-hidden="true">` +
  `<circle cx="6" cy="19" r="3"/>` +
  `<path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"/>` +
  `<circle cx="18" cy="5" r="3"/>` +
  `</svg>`

const WAYPOINT_ICON_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" ` +
  `stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" ` +
  `style="color:#111827;flex-shrink:0;" aria-hidden="true">` +
  `<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>` +
  `</svg>`

const CAMERA_ICON_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" ` +
  `stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" ` +
  `style="color:#111827;flex-shrink:0;" aria-hidden="true">` +
  `<path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/>` +
  `<circle cx="12" cy="13" r="3"/>` +
  `</svg>`

const chip = (icon: string, value: number) =>
  `<span style="display:flex;align-items:center;gap:4px;">${icon}<span style="color:#111827;">${value}</span></span>`

const row = (items: string[], marginTop = '2px') =>
  `<div class="text-xs" style="display:flex;align-items:center;gap:8px;margin-top:${marginTop};">${items.join('')}</div>`

const truncatePopupText = (value: string, maxLength = 28) =>
  value.length > maxLength ? `${value.slice(0, maxLength)}...` : value

const buildPrimaryTextRow = (content: string) =>
  `<div class="text-sm font-semibold" style="max-width:260px;color:#111827;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${content}</div>`

const buildSecondaryTextRow = (content: string) =>
  `<div class="text-xs" style="max-width:260px;color:#111827;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:2px;">${content}</div>`

export const formatWorkspacePopupDateRange = (
  start?: string | null,
  end?: string | null,
): string | null => {
  if (!start && !end) return null

  const fmtFull = (value: Date) =>
    value.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  const fmtNoYear = (value: Date) =>
    value.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })

  if (start && end) {
    const startDate = new Date(start)
    const endDate = new Date(end)

    if (startDate.toDateString() === endDate.toDateString()) {
      return fmtFull(startDate)
    }
    if (startDate.getFullYear() === endDate.getFullYear()) {
      return `${fmtNoYear(startDate)} - ${fmtFull(endDate)}`
    }
    return `${fmtFull(startDate)} - ${fmtFull(endDate)}`
  }

  return fmtFull(new Date(start ?? end!))
}

export function buildWorkspaceCollectionHoverPopupHtml(input: {
  name: string
  ownerNames?: string[] | null
  isOwner?: boolean
  dateRange?: string | null
  coverPhotoThumbnailUrl?: string | null
  tourCount: number
  poiCount?: number | null
  photoCount: number
}): string {
  const safeName = escapeHtml(truncatePopupText(input.name.trim() || 'Untitled Collection'))
  const ownerByline =
    !input.isOwner && input.ownerNames && input.ownerNames.length > 0
      ? ` <span class="font-normal">by ${escapeHtml(
          truncatePopupText(input.ownerNames.join(', '), 22),
        )}</span>`
      : ''

  const countItems: string[] = []
  if (input.tourCount > 0) {
    countItems.push(chip(ROUTE_ICON_SVG, input.tourCount))
  }
  if (typeof input.poiCount === 'number' && input.poiCount > 0) {
    countItems.push(chip(WAYPOINT_ICON_SVG, input.poiCount))
  }
  if (input.photoCount > 0) {
    countItems.push(chip(CAMERA_ICON_SVG, input.photoCount))
  }

  const rows = [buildPrimaryTextRow(`${safeName}${ownerByline}`)]
  if (input.dateRange) {
    rows.push(buildSecondaryTextRow(escapeHtml(input.dateRange)))
  }
  if (countItems.length > 0) {
    rows.push(row(countItems))
  }

  const textHtml = rows.join('')
  if (!input.coverPhotoThumbnailUrl) {
    return textHtml
  }

  const thumbnailHtml =
    `<img src="${escapeHtml(input.coverPhotoThumbnailUrl)}" ` +
    `style="width:56px;height:56px;object-fit:cover;border-radius:6px;flex-shrink:0;" alt="" />`

  return (
    `<div style="display:flex;align-items:center;gap:10px;">` +
    `${thumbnailHtml}` +
    `<div style="display:flex;flex-direction:column;justify-content:center;min-height:56px;">${textHtml}</div>` +
    `</div>`
  )
}
